/**
 * Pendo analytics utilities
 * @see https://support.pendo.io/hc/en-us/articles/360031862272-Install-Pendo-on-a-single-page-web-application
 */

import { isHeadTrainer, isSubTrainer } from 'src/models/user/utils';

import type { Session } from 'src/models/session/_types';
import type { User } from 'src/models/user/_types';

// Pendo API key
const PENDO_API_KEY = import.meta.env.VITE_PENDO_API_KEY || '';

// ============================================================================
// Types
// ============================================================================

export interface PendoConfig {
  visitor: {
    id: string;
    email?: string;
    full_name?: string;
    role?: string;
  };
  account: {
    id: string;
    name?: string;
    cloudflare_configured?: boolean;
    app_store_health?: string;
    app_store_customer_action_required?: boolean;
  };
  guides?: {
    disabled: boolean;
  };
}

interface PendoInstance {
  initialize: (config: PendoConfig) => void;
  identify: (config: PendoConfig) => void;
  updateOptions: (options: Partial<PendoConfig>) => void;
  pageLoad: () => void;
  track: (eventName: string, metadata?: Record<string, unknown>) => void;
  trackAgent: (eventName: string, metadata?: Record<string, unknown>) => void;
  _q?: unknown[][];
}

declare global {
  interface Window {
    __PLAYWRIGHT_E2E__?: boolean;
    pendo?: PendoInstance;
  }
}

// ============================================================================
// Functions
// ============================================================================

/**
 * Load the Pendo agent script following the official SPA installation pattern.
 * This creates stub methods that queue calls until the agent is loaded.
 */
export function loadPendoScript(apiKey: string = PENDO_API_KEY): void {
  if (window.pendo) return; // Already loaded
  // A blank key builds a 404 URL, which app webviews report as a load failure.
  if (!apiKey.trim()) return;

  // Official Pendo loader pattern - creates stub methods that queue calls
  const pendo = (window.pendo = window.pendo || ({} as PendoInstance));
  pendo._q = pendo._q || [];

  // Stub all Pendo methods to queue calls until the agent loads
  const methods = [
    'initialize',
    'identify',
    'updateOptions',
    'pageLoad',
    'track',
    'trackAgent',
  ] as const;
  methods.forEach((method) => {
    if (!pendo[method]) {
       
      (pendo as any)[method] = function (...args: unknown[]) {
        // 'initialize' uses unshift (priority), others use push
        pendo._q?.[method === 'initialize' ? 'unshift' : 'push']([
          method,
          ...args,
        ]);
      };
    }
  });

  // Load the Pendo agent script
  const script = document.createElement('script');
  script.id = 'pendo-script';
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;

  // Insert before first script tag (official pattern)
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Get the user role for Pendo
 * - owner: head trainer (platform owner)
 * - staff: sub trainer
 * - client: regular user
 */
export function getPendoUserRole(user: User | null | undefined): string {
  if (!user) return 'anonymous';
  if (isHeadTrainer(user)) return 'owner';
  if (isSubTrainer(user)) return 'staff';
  return 'client';
}

/**
 * Get environment prefix for Pendo IDs.
 * This keeps dev/staging data separate from production in Pendo analytics.
 * @see https://support.pendo.io/hc/en-us/articles/360031862352-Pendo-in-multiple-environments-for-development-and-testing
 */
function getEnvironmentPrefix(): string {
  const mode = import.meta.env.MODE;
  if (mode === 'production') return '';
  if (mode === 'staging') return 'staging_';
  return 'dev_';
}

/**
 * Build the Pendo configuration object from user and session data.
 * In non-production environments, IDs are prefixed to keep data separate.
 */
export function buildPendoConfig(user: User, session: Session): PendoConfig {
  const prefix = getEnvironmentPrefix();

  return {
    visitor: {
      id: `${prefix}${user.id}`,
      email: user.email,
      full_name:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : undefined,
      role: getPendoUserRole(user),
    },
    account: {
      id: `${prefix}${session.id}`,
      name: session.brand || undefined,
      cloudflare_configured: session.cloudflareConfigured,
      app_store_health: session.appStoreHealthStatus || undefined,
      app_store_customer_action_required:
        session.appStoreCustomerActionRequired,
    },
    ...(window.__PLAYWRIGHT_E2E__
      ? {
          guides: {
            disabled: true,
          },
        }
      : {}),
  };
}

/**
 * Initialize Pendo with user and session data
 */
export function initializePendo(user: User, session: Session): void {
  const config = buildPendoConfig(user, session);
  window.pendo?.initialize(config);
}

/**
 * Update Pendo identity when user changes (e.g., login/logout)
 */
export function identifyPendo(user: User, session: Session): void {
  const config = buildPendoConfig(user, session);
  window.pendo?.identify(config);
}

/**
 * Track a custom event in Pendo
 */
export function trackPendoEvent(
  eventName: string,
  metadata?: Record<string, unknown>
): void {
  window.pendo?.track(eventName, metadata);
}

export { PENDO_API_KEY };
